import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  BarChart3,
  Calculator,
  Landmark,
  Wallet,
  ArrowDownLeft,
  ArrowUpRight,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { money0 } from "../utils/money";
import "./DailyOverview.css";
const titleMonth = (month) =>
  new Date(`${month}-01T12:00:00`).toLocaleDateString("en-MY", {
    month: "long",
    year: "numeric",
  });
const timestamp = (value) =>
  value
    ? new Date(value).toLocaleString("en-MY", {
        timeZone: "Asia/Kuala_Lumpur",
        dateStyle: "medium",
        timeStyle: "short",
      }) + " MYT"
    : "Awaiting first successful sync";
function Topic({ type }) {
  const Icon = {
    sales: BarChart3,
    profit: Calculator,
    bank: Landmark,
    collect: Wallet,
    pay: Wallet,
  }[type];
  return (
    <span className={`daily-topic ${type}`}>
      <Icon size={22} aria-hidden="true" />
      {["collect", "pay"].includes(type) && (
        <span className="daily-direction">
          {type === "collect" ? (
            <ArrowDownLeft size={12} />
          ) : (
            <ArrowUpRight size={12} />
          )}
        </span>
      )}
    </span>
  );
}
function Card({ type, label, value, note, onClick, incomplete }) {
  return (
    <section
      className={`daily-kpi ${type} ${type === "profit" ? "highlight" : ""}`}
      aria-label={label}
    >
      <div className="daily-kpi-heading">
        <Topic type={type} />
        <h3>{label}</h3>
      </div>
      <strong className={value < 0 ? "daily-negative" : ""}>
        {incomplete ? "Incomplete" : money0(value)}
      </strong>
      <p>{note}</p>
      <button
        className="daily-link"
        onClick={onClick}
        aria-label={`View ${label.toLowerCase()} details`}
      >
        View details <ArrowUpRight size={14} />
      </button>
    </section>
  );
}
function compactRM(value) {
  if (value === 0) return "RM0";
  const sign = value < 0 ? "−" : "";
  const amount = Math.abs(value);
  return amount >= 1000000
    ? `${sign}RM${(amount / 1000000).toFixed(1)}m`
    : `${sign}RM${Math.round(amount / 1000)}k`;
}
function TrendChart({ trend }) {
  const rows = trend.slice(-6);
  const salesMax = Math.max(
    1200000,
    Math.ceil(Math.max(...rows.map((row) => Math.abs(row.sales || 0))) / 200000) *
      200000,
  );
  const profitMax = Math.max(
    240000,
    Math.ceil(Math.max(...rows.map((row) => Math.abs(row.profit || 0))) / 120000) *
      120000,
  );
  const salesBase = 166,
    salesTop = 50,
    profitBase = 286,
    profitTop = 220;
  const salesScale = (salesBase - salesTop) / salesMax;
  const profitScale = (profitBase - profitTop) / profitMax;
  const salesTicks = [0, salesMax / 2, salesMax];
  const profitTicks = [-profitMax, -profitMax / 2, 0, profitMax / 2, profitMax];
  return (
    <div className="daily-svg-wrap">
      <svg
        className="daily-svg-chart"
        viewBox="0 0 800 395"
        role="img"
        aria-label="Six-month sales and profit chart"
      >
        <text x="70" y="20" fontSize="11" fontWeight="650" fill="#69798e">
          SALES
        </text>
        <text x="70" y="201" fontSize="11" fontWeight="650" fill="#69798e">
          PROFIT BEFORE TAX
        </text>
        {salesTicks.map((value) => {
          const y = salesBase - value * salesScale;
          return (
            <g key={`sales-${value}`}>
              <line x1="70" x2="748" y1={y} y2={y} stroke="#e8edf4" />
              <text x="60" y={y + 4} textAnchor="end" fontSize="10" fill="#8793a4">
                {compactRM(value)}
              </text>
            </g>
          );
        })}
        <line x1="70" x2="748" y1={profitBase} y2={profitBase} stroke="#9aa4b3" strokeWidth="1.5" />
        {profitTicks.map((value) => {
          const y = profitBase - value * profitScale;
          return (
            <g key={`profit-${value}`}>
              <line x1="70" x2="748" y1={y} y2={y} stroke="#eef1f6" />
              <text x="60" y={y + 4} textAnchor="end" fontSize="10" fill="#8793a4">
                {compactRM(value)}
              </text>
            </g>
          );
        })}
        {rows.map((row, index) => {
          const x = 88 + index * 110;
          const sales = row.sales || 0;
          const profit = row.profit;
          const salesHeight = Math.abs(sales) * salesScale;
          const profitHeight = Math.abs(profit || 0) * profitScale;
          const salesY = salesBase - salesHeight;
          const profitY = profit >= 0 ? profitBase - profitHeight : profitBase;
          const salesFill = row.source === "live" ? "#756de1" : "#66508a";
          const profitFill = profit < 0 ? "#dc2626" : "#2f855a";
          const labelY = profit == null ? profitBase - 8 : profit >= 0 ? profitY - 8 : profitY + profitHeight + 16;
          return (
            <g key={row.month}>
              {row.sales != null && <rect x={x} y={salesY} width="58" height={salesHeight} rx="5" fill={salesFill} />}
              <text x={x + 29} y={Math.max(30, salesY - 9)} textAnchor="middle" fontSize="11" fontWeight="600" fill="#26364d">
                {row.sales == null ? "—" : money0(row.sales)}
              </text>
              {profit != null && <rect x={x} y={profitY} width="58" height={profitHeight} rx="5" fill={profitFill} />}
              <text className={profit < 0 ? "daily-negative" : ""} x={x + 29} y={labelY} textAnchor="middle" fontSize="11" fontWeight="600" fill={profit == null ? "#26364d" : profitFill}>
                {profit == null ? "—" : money0(profit)}
              </text>
              <text x={x + 29} y="378" textAnchor="middle" fontSize="11" fill="#8390a1">
                {titleMonth(row.month).split(" ")[0].slice(0, 3)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
export default function DailyOverview({ month, mode = "overview" }) {
  const { token, isSuperadmin } = useAuth();
  const [data, setData] = useState(null),
    [settings, setSettings] = useState(null),
    [bank, setBank] = useState(null),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [busy, setBusy] = useState(false),
    [retry, setRetry] = useState(0),
    [dialog, setDialog] = useState(null),
    [review, setReview] = useState(null),
    [tab, setTab] = useState("rates"),
    [source, setSource] = useState("");
  const headers = { Authorization: `Bearer ${token}` };
  useEffect(() => {
    let cancel = false;
    setData((previous) => (previous?.month === month ? previous : null));
    setError("");
    setDialog(null);
    setReview(null);
    Promise.allSettled([
      axios.get(`/api/daily?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      isSuperadmin
        ? axios.get(`/api/bank-balances?month=${month}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        : Promise.resolve(null),
    ]).then(([main, cash]) => {
      if (cancel) return;
      if (main.status === "fulfilled") {
        setData(main.value.data);
        setSettings(main.value.data.settings);
      } else
        setError(
          main.reason.response?.data?.error ||
            "The overview could not be loaded. Try again.",
        );
      if (cash.status === "fulfilled") setBank(cash.value?.data);
    });
    return () => {
      cancel = true;
    };
  }, [month, token, isSuperadmin, retry, mode]);
  useEffect(() => {
    if (!dialog) return;
    const el = document.getElementById("daily-dialog");
    el?.showModal();
    return () => el?.close();
  }, [dialog]);
  async function action(path, payload = {}, reload = true, method = "post") {
    setBusy(true);
    setNotice("");
    setError("");
    try {
      const r = await axios[method](
        `/api/daily/${path}`,
        { month, ...payload },
        { headers },
      );
      setNotice(r.data.message || "Saved successfully.");
      if (reload) setRetry((n) => n + 1);
      return r.data;
    } catch (e) {
      setError(
        e.response?.data?.error ||
          "The request failed. Your last saved figures are unchanged.",
      );
      return null;
    } finally {
      setBusy(false);
    }
  }
  async function reviewClose() {
    setBusy(true);
    try {
      const r = await axios.get(`/api/daily/review?month=${month}`, {
        headers,
      });
      setReview(r.data);
      setDialog("close");
    } catch (e) {
      setError("Unable to load closing review. Try again.");
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    if (mode !== "overview" || dialog) return;
    const timer = setInterval(() => setRetry((n) => n + 1), 60000);
    return () => clearInterval(timer);
  }, [mode, dialog]);
  const heading = {
    overview: [
      "Your business, at a glance.",
      "Sales, profit and your latest cash position.",
    ],
    settings: [
      "Forecast settings",
      "Set the assumptions used to estimate profit. Only superadmins can save changes.",
    ],
    closing: [
      "Management month closing",
      "Review actuals, then publish a final version. Entering actuals alone does not close the month.",
    ],
    checks: [
      "Data checks",
      "Correct missing business assignments in Bukku, then refresh the sales feed.",
    ],
    customers: [
      "Customer balances",
      "Open customer invoices awaiting payment.",
    ],
    suppliers: [
      "Supplier balances",
      "Open supplier bills and claims awaiting payment.",
    ],
  }[mode];
  if (!data)
    return (
      <div className="daily-workspace">
        <h2>{heading[0]}</h2>
        {error ? (
          <div role="alert">
            {error}{" "}
            <button onClick={() => setRetry((n) => n + 1)}>Try again</button>
          </div>
        ) : (
          <p role="status">Loading {titleMonth(month)}…</p>
        )}
      </div>
    );
  const final = !!data.published,
    sales = final ? data.published.sales : data.live.sales,
    profit = final ? data.published.profit : data.live.profit;
  const collect = data.outstanding?.collect || null;
  const pay = data.outstanding?.pay || null;
  const locked = data.state.status === "closed",
    stale =
      data.syncedAt &&
      Date.now() - new Date(data.syncedAt).getTime() > 36 * 60 * 60 * 1000;
  const bankValid =
    bank?.accounts?.length > 0 &&
    bank.accounts.every(
      (a) => a.currency === "MYR" && a.balance != null && a.synced_at,
    );
  const totalBank = bankValid ? bank.total : null;
  const status = locked
    ? "Final · Management accounts"
    : data.state.status === "revision"
      ? "Under revision · Previous final"
      : "Live sales · Estimated profit";
  const pct =
    sales === null
      ? "Awaiting first sales sync"
      : data.targets.sales > 0
        ? Math.round((sales / data.targets.sales) * 100) + "% of sales target"
        : "Sales target not set";
  function businessTable() {
    return (
      <div className="daily-scroll">
        <table>
          <thead>
            <tr>
              <th>Business</th>
              <th>{final ? "Final sales" : "Recorded sales"}</th>
              <th>Sales target</th>
              <th>Achieved</th>
            </tr>
          </thead>
          <tbody>
            {data.metadata.businesses.map(([key, label, line]) => {
              const v = final
                ? key === "wnexus"
                  ? [
                      "rev_wnexus_1utama",
                      "rev_wnexus_kelana",
                      "rev_wnexus_kl",
                    ].reduce(
                      (s, k) =>
                        s + Number(data.published.values[k]?.actual || 0),
                      0,
                    )
                  : (data.published.values[line]?.actual ?? null)
                : data.live.sales === null
                  ? null
                  : data.live.totals[key];
              const target = data.targets.businesses[key];
              return (
                <tr key={key}>
                  <th>{label}</th>
                  <td>{money0(v)}</td>
                  <td>{money0(target)}</td>
                  <td>
                    {target > 0 && v !== null
                      ? Math.round((v / target) * 100) + "%"
                      : "—"}
                  </td>
                </tr>
              );
            })}
            {!final &&
              ["adhoc", "unmatched", "inactive"].map((key) => (
                <tr key={key}>
                  <th>
                    {
                      {
                        adhoc: "Ad hoc · Outsource Marketing",
                        unmatched: "Business not matched",
                        inactive: "Inactive businesses",
                      }[key]
                    }
                  </th>
                  <td>
                    {money0(
                      data.live.sales === null ? null : data.live.totals[key],
                    )}
                  </td>
                  <td colSpan={2}>
                    {key === "adhoc"
                      ? "Excluded from estimate and targets"
                      : "Requires review if present"}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    );
  }
  const setValue = (section, index, value) => {
    const n = value === "" ? null : Number(value);
    setSettings((s) => {
      const values = [...s[section]];
      values[index] = n;
      return { ...s, [section]: values };
    });
  };
  const rateInput = (section, index, label) => (
    <input
      aria-label={label}
      type="number"
      min="0"
      max={section === "fixed" ? 1000000000 : 100}
      step="0.01"
      value={settings[section][index] ?? ""}
      disabled={locked || busy || !isSuperadmin}
      onChange={(e) => setValue(section, index, e.target.value)}
      placeholder="Not set"
    />
  );
  const numericTotal = (values) =>
    Math.round(
      (values || []).reduce(
        (total, value) => total + (typeof value === "number" ? value : 0),
        0,
      ) * 100,
    ) / 100;
  const groupRateTotal = numericTotal(settings?.rates);
  const fixedCostTotal = numericTotal(settings?.fixed);
  const outstandingDetails = (kind) => {
    const isCollect = kind === "collect";
    const summary = isCollect ? collect : pay;
    const documents = data.outstanding?.documents?.[kind] || [];
    const subject = isCollect ? "customer invoices" : "supplier bills and claims";
    const contact = isCollect ? "Customer" : "Supplier / claimant";
    if (!summary) {
      return (
        <p>
          No verified outstanding snapshot is available yet. The next successful
          Bukku refresh will keep the last complete position if another source
          fails.
        </p>
      );
    }
    return (
      <>
        <p>
          <strong>{money0(summary.total)}</strong> across {summary.documents} open {subject}.
          Current document balances from Bukku, updated {timestamp(data.outstanding.syncedAt)}.
        </p>
        <div className="daily-scroll">
          <table>
            <thead>
              <tr>
                <th>Company</th>
                <th>Document</th>
                <th>Date</th>
                <th>{contact}</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((row) => (
                <tr key={`${row.company}-${row.id}`}>
                  <td>{row.company}</td>
                  <th>{row.number}</th>
                  <td>{row.date}</td>
                  <td>{row.contactName}</td>
                  <td>{money0(row.balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };
  return (
    <div className="daily-workspace">
      <div className="daily-heading">
        <div>
          <h2>{heading[0]}</h2>
          <p>{heading[1]}</p>
        </div>
        <span>{titleMonth(month)}</span>
        {mode === "overview" && isSuperadmin && (
          <button disabled={busy} onClick={() => action("refresh", {}, false)}>
            <RefreshCw size={16} />
            Refresh dashboard data
          </button>
        )}
      </div>
      {error && (
        <div className="daily-notice warning" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="daily-notice" role="status">
          {notice} <Link to="/sync">Sync status</Link>
        </div>
      )}
      {mode === "overview" && (
        <>
          <div className="daily-status">
            <span>{status}</span>
            <button className="daily-link" onClick={() => setDialog("sources")}>
              How these numbers work
            </button>
          </div>
          <div
            className={`daily-notice ${profit === null || stale ? "warning" : ""}`}
          >
            <AlertCircle size={20} />
            <div>
              <strong>
                {final
                  ? "Your published management result."
                  : profit === null
                    ? "Your profit estimate needs attention."
                    : profit >= 0
                      ? "Recorded sales cover the month’s estimated costs."
                      : "Recorded sales have not yet covered the month’s estimated costs."}
              </strong>
              <p>
                {final
                  ? "Cash and outstanding balances always show the latest available position."
                  : profit === null
                    ? `${data.live.missing.length} assumptions not set · ${data.live.checks.length ? `${data.live.checks.length} business assignments to review` : "Business assignments are complete"}${data.syncedAt ? "" : " · first successful sales sync needed"}.`
                    : `Estimated profit excludes ${money0(data.live.totals.adhoc)} ad hoc sales and includes the full monthly fixed-cost budget.`}
              </p>
            </div>
          </div>
          <p className="daily-timestamp">
            Sales feed updated {timestamp(data.syncedAt)}
            {stale
              ? " · Refresh needed; previous successful figures shown."
              : ""}
          </p>
          <div className="daily-kpi-groups">
            <section className="daily-kpi-group" aria-labelledby="business-performance-heading">
              <h3 id="business-performance-heading">Business performance</h3>
              <div className="daily-kpis daily-kpis-performance">
            <Card
              type="sales"
              label="Sales"
              value={sales}
              note={pct}
              onClick={() => setDialog("sales")}
            />
            <Card
              type="profit"
              label={final ? "Final profit" : "Estimated profit"}
              value={profit}
              incomplete={!final && profit === null}
              note={
                final
                  ? "Before tax · final actuals"
                  : "Before tax · excludes ad hoc"
              }
              onClick={() => setDialog("profit")}
            />
              </div>
            </section>
            <section className="daily-kpi-group" aria-labelledby="cash-flow-heading">
              <h3 id="cash-flow-heading">Cash flow</h3>
              <div className="daily-kpis daily-kpis-cash">
            <Card
              type="bank"
              label="Cash in bank"
              value={totalBank}
              note={
                bankValid
                  ? `${bank.accounts.length} bank account${bank.accounts.length === 1 ? "" : "s"}`
                  : "Balance unavailable"
              }
              onClick={() => setDialog("bank")}
            />
            <Card
              type="collect"
              label="To collect"
              value={collect?.total ?? null}
              note={
                collect
                  ? `${collect.documents} open customer invoice${collect.documents === 1 ? "" : "s"}`
                  : "Outstanding snapshot unavailable"
              }
              onClick={() => setDialog("customers")}
            />
            <Card
              type="pay"
              label="To pay"
              value={pay?.total ?? null}
              note={
                pay
                  ? `${pay.documents} open bill${pay.documents === 1 ? "" : "s"} and claims`
                  : "Outstanding snapshot unavailable"
              }
              onClick={() => setDialog("suppliers")}
            />
              </div>
            </section>
          </div>
          <div className="daily-columns">
            <section className="daily-panel">
              <div className="daily-trend-head">
                <div>
                  <h3>Sales over six months</h3>
                  <p>Sales stay on their own scale; profit has a separate lane for easy reading.</p>
                </div>
                <div className="daily-trend-legend" aria-label="Chart legend">
                  <span><i className="sales" />Sales</span>
                  <span><i className="profit" />Profit before tax</span>
                </div>
              </div>
              <TrendChart trend={data.trend} />
              <p className="daily-trend-note">Final months use published actuals. Open months use the current full-month estimate. Positive profit is green; negative profit drops below zero in strong red.</p>
            </section>
            <section className="daily-panel">
              <h3>What needs your attention</h3>
              <ul className="daily-attention">
                <li>
                  <strong>
                    {data.live.checks.length
                      ? `${data.live.checks.length} business assignments to review`
                      : "Business assignments are complete"}
                  </strong>
                  <p>
                    Fill in the business field in Bukku for blank invoices.
                    Review inactive business activity separately.
                  </p>
                  <Link to="/overview/checks">Review data checks ↗</Link>
                </li>
                {!final && (
                  <li>
                    <strong>
                      {data.live.missing.length
                        ? `${data.live.missing.length} forecast assumptions not set`
                        : "Forecast assumptions are complete"}
                    </strong>
                    <p>
                      Enter a rate for each category. Use zero when no cost
                      applies.
                    </p>
                    {isSuperadmin && (
                      <Link to="/settings/daily-forecast">
                        Complete forecast settings ↗
                      </Link>
                    )}
                  </li>
                )}
                <li>
                  <strong>
                    {sales === null
                      ? "Awaiting first sales sync"
                      : data.targets.sales > 0
                        ? money0(Math.max(0, data.targets.sales - sales)) +
                          " remaining to sales target"
                        : "Sales target comparison unavailable"}
                  </strong>
                  <Link to="/forecast">Review targets ↗</Link>
                </li>
              </ul>
            </section>
          </div>
          <section className="daily-panel">
            <h3>Sales by business</h3>
            <p>
              Nexus branches share one target. Discounts and other management
              adjustments are shown in the full P&amp;L.
            </p>
            {businessTable()}
          </section>
        </>
      )}
      {mode === "settings" && (
        <>
          <div className="daily-notice">
            Percentage rates apply once to total active business revenue. Ad
            hoc is excluded. A negative revenue base stops at zero. Fixed
            costs apply for the full month.
          </div>
          {locked && (
            <p className="daily-notice warning">
              This month is closed.{" "}
              <Link to="/settings/month-closing">
                Reopen it before editing.
              </Link>
            </p>
          )}
          <div className="daily-tabs">
            {[
              ["rates", "Group rates (%)"],
              ["fixed", "Fixed costs (RM)"],
            ].map(([k, t]) => (
              <button
                key={k}
                aria-pressed={tab === k}
                onClick={() => setTab(k)}
              >
                {t}
              </button>
            ))}
          </div>
          <section className="daily-panel">
            <div className="daily-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Cost category</th>
                    <th>
                      {tab === "rates" ? "% of group revenue" : "Monthly RM"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(tab === "rates"
                    ? data.metadata.rates
                    : data.metadata.fixed
                  ).map((label, i) => (
                    <tr key={label}>
                      <th>{label}</th>
                      <td>{rateInput(tab, i, label)}</td>
                    </tr>
                  ))}
                </tbody>
                {["rates", "fixed"].includes(tab) && (
                  <tfoot>
                    <tr className="daily-settings-total">
                      <th>
                        {tab === "rates"
                          ? "Total group rates"
                          : "Total fixed costs"}
                      </th>
                      <td>
                        {tab === "rates"
                          ? `${groupRateTotal}%`
                          : money0(fixedCostTotal)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <p>
              Blank means not set. Zero means no cost. Targets are maintained
              separately in Actuals Input.
            </p>
            <button
              disabled={locked || busy || !isSuperadmin}
              onClick={() =>
                action(
                  "settings",
                  { settings, revision: data.state.revision },
                  true,
                  "put",
                )
              }
            >
              Save assumptions
            </button>
          </section>
          <section className="daily-panel">
            <h3>Copy from an earlier month</h3>
            <p>
              Copies saved rates and fixed budgets into this month only when it
              has no settings. Actuals and targets are not copied.
            </p>
            <input
              type="month"
              aria-label="Copy from month"
              value={source}
              onChange={(e) => setSource(e.target.value)}
            />
            <button
              disabled={busy || !source || locked}
              onClick={() => action("copy", { from: source })}
            >
              Copy into {titleMonth(month)}
            </button>
          </section>
        </>
      )}
      {mode === "closing" && (
        <>
          <section className="daily-panel">
            <span className="daily-status-pill">{status}</span>
            <h3>
              {locked
                ? "This month is closed"
                : data.state.status === "revision"
                  ? "Revision in progress"
                  : "Review the management accounts"}
            </h3>
            <p>
              Use the existing full P&amp;L form to enter actuals, including ad
              hoc costs and profit. The close review requires an entered amount
              in every section; confirm the detailed lines are complete before
              publishing.
            </p>
            <Link to="/settings/actuals">Open Actuals Input ↗</Link>
            <div className="daily-close-values">
              <span>
                Draft sales <strong>{money0(data.closingPreview.sales)}</strong>
              </span>
              <span>
                Draft costs <strong>{money0(data.closingPreview.costs)}</strong>
              </span>
              <span>
                Draft profit before tax{" "}
                <strong>{money0(data.closingPreview.profit)}</strong>
              </span>
            </div>
            {locked ? (
              <button disabled={busy} onClick={() => setDialog("reopen")}>
                Reopen for revision
              </button>
            ) : (
              <button disabled={busy} onClick={reviewClose}>
                Review and close month
              </button>
            )}
          </section>
          <section className="daily-panel">
            <h3>Published versions</h3>
            {!data.history.length ? (
              <p>No final version has been published.</p>
            ) : (
              <div className="daily-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Version</th>
                      <th>Published</th>
                      <th>Sales</th>
                      <th>Profit before tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.history.map((h) => (
                      <tr key={h.version}>
                        <th>Version {h.version}</th>
                        <td>{timestamp(h.closed_at)}</td>
                        <td>{money0(h.sales)}</td>
                        <td>{money0(h.profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
      {mode === "checks" && (
        <section className="daily-panel">
          <h3>Invoices that need review</h3>
          <p>
            Sales remain included. Profit stays incomplete until business
            assignments and inactive activity are resolved.
          </p>
          {!data.syncedAt ? (
            <p>Waiting for the first complete sales sync.</p>
          ) : !data.live.checks.length ? (
            <p>
              No unmatched or inactive business activity in this month’s synced
              invoices.
            </p>
          ) : (
            <div className="daily-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Invoice date</th>
                    <th>Customer</th>
                    <th>Salesperson</th>
                    <th>Business / issue</th>
                    <th>Sales (RM)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.live.checks.map((r) => (
                    <tr key={`${r.company}-${r.id}`}>
                      <th>
                        {r.bukkuUrl ? (
                          <a href={r.bukkuUrl} target="_blank" rel="noreferrer">
                            {r.invoice_number} ↗
                          </a>
                        ) : (
                          r.invoice_number
                        )}
                      </th>
                      <td>{r.invoice_date}</td>
                      <td>{r.customer_name}</td>
                      <td>{r.salesperson || "Unassigned"}</td>
                      <td>
                        {r.venue || "Business field blank"} · {r.issue}
                      </td>
                      <td>{money0(r.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p>
            Open the matching document in Bukku and correct the business field,
            then use Refresh dashboard data on Overview.
          </p>
          <Link to="/sync">Check sync progress ↗</Link>
        </section>
      )}
      {["customers", "suppliers"].includes(mode) && (
        <section className="daily-panel">
          <h3>{mode === "customers" ? "Open customer invoices" : "Open bills and claims"}</h3>
          {outstandingDetails(mode === "customers" ? "collect" : "pay")}
        </section>
      )}
      {dialog && (
        <dialog
          id="daily-dialog"
          className="daily-dialog"
          onCancel={() => setDialog(null)}
        >
          <div className="daily-dialog-head">
            <h2>
              {
                {
                  sales: "Sales details",
                  profit: "How profit adds up",
                  bank: "Cash in bank",
                  customers: "Customer balances",
                  suppliers: "Supplier balances",
                  sources: "How these numbers work",
                  close: "Review final management figures",
                  reopen: "Reopen this month?",
                }[dialog]
              }
            </h2>
            <button aria-label="Close details" onClick={() => setDialog(null)}>
              ×
            </button>
          </div>
          {dialog === "sales" && (
            <>
              <p>
                {final
                  ? "Published final management figures."
                  : "Bukku sales use the invoice date for the full selected month. Invoice amounts follow the existing sales calculation. Credits reduce sales; payments received are separate."}
              </p>
              {businessTable()}
            </>
          )}
          {dialog === "profit" &&
            (final ? (
              <p>
                Published sales {money0(sales)} − costs{" "}
                {money0(data.published.costs)} = profit before tax{" "}
                {money0(profit)}. Includes final ad hoc contribution.
              </p>
            ) : (
              <>
                <p>
                  Active business sales {money0(data.live.eligible)} − full
                  estimated costs {money0(data.live.cost)}. Ad hoc sales are
                  excluded.{" "}
                  {data.live.missing.length > 0 &&
                    "Missing assumptions keep profit incomplete."}
                </p>
                <div className="daily-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Business</th>
                        <th>Cost category</th>
                        <th>Rate</th>
                        <th>Estimated RM</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.live.costs.map((r, i) => (
                        <tr key={i}>
                          <th>{r.business}</th>
                          <td>{r.category}</td>
                          <td>{r.rate == null ? "—" : r.rate + "%"}</td>
                          <td>{money0(r.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ))}
          {dialog === "bank" && (
            <>
              <p>
                Latest selected MYR accounts, independent of the selected
                P&amp;L month.
              </p>
              {bank?.accounts?.length ? (
                <div className="daily-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Account</th>
                        <th>Balance</th>
                        <th>Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bank.accounts.map((a) => (
                        <tr key={`${a.company}-${a.bukku_code}`}>
                          <th>
                            {a.company} · {a.name}
                          </th>
                          <td>
                            {a.currency} {a.balance}
                          </td>
                          <td>{timestamp(a.synced_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>
                  Balances are unavailable for this account or no accounts have
                  been selected.
                </p>
              )}
              {isSuperadmin && (
                <Link
                  to="/settings/bank-accounts"
                  onClick={() => setDialog(null)}
                >
                  Choose bank accounts ↗
                </Link>
              )}
            </>
          )}
          {["customers", "suppliers"].includes(dialog) && (
            outstandingDetails(dialog === "customers" ? "collect" : "pay")
          )}
          {dialog === "sources" && (
            <>
              <p>
                <strong>Open month:</strong> full-month invoice-date Bukku
                sales. Profit uses manually configured business and group
                percentages plus full monthly fixed costs; excludes ad hoc
                activity.
              </p>
              <p>
                <strong>Closed month:</strong> sales and profit use the
                published management actuals. Reopening keeps the previous
                published result until the next close.
              </p>
              <p>
                <strong>Cash and balances:</strong> latest position, never a
                historical month-end amount. Outstanding totals use Bukku’s
                open invoice and bill documents; a failed balance sync retains
                the previous complete snapshot.
              </p>
            </>
          )}
          {dialog === "reopen" && (
            <>
              <p>
                The previous final version remains published while you revise
                actuals and assumptions. A new close creates the next version.
              </p>
              <button
                disabled={busy}
                onClick={async () => {
                  if (await action("reopen", { revision: data.state.revision }))
                    setDialog(null);
                }}
              >
                Reopen for revision
              </button>
            </>
          )}
          {dialog === "close" && review && (
            <>
              <p>
                Confirm the full management P&amp;L is complete, including ad
                hoc activity. This publishes a saved version and locks editing.
              </p>
              <p>
                Sales <strong>{money0(review.sales)}</strong> · Costs{" "}
                <strong>{money0(review.costs)}</strong> · Profit before tax{" "}
                <strong>{money0(review.profit)}</strong>
              </p>
              {review.missing.length > 0 && (
                <p role="alert">
                  Missing sections: {review.missing.join(", ")}
                </p>
              )}
              <div className="daily-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Actual RM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {review.tree.sections.flatMap((s) =>
                      s.lines.map((l) => (
                        <tr key={l.key}>
                          <th>{l.label}</th>
                          <td>{money0(l.actual)}</td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
              <button
                disabled={busy || review.missing.length > 0}
                onClick={async () => {
                  if (
                    await action("close", {
                      revision: data.state.revision,
                      reviewedValues: review.values,
                    })
                  )
                    setDialog(null);
                }}
              >
                Confirm complete and publish final
              </button>
            </>
          )}
          {error && <p role="alert">{error}</p>}
        </dialog>
      )}
    </div>
  );
}
